import React, { useState, useEffect, useRef } from 'react';
import localStorageUtil from '../../../../utils/localStorageUtil';
import './Chat.scss';
import ChatSidebar from './ChatSidebar';
import ChatWindow from './ChatWindow';
import { 
    fetchConversations, 
    searchConversation, 
    loadMessageHistory, 
    sendMessage, 
    markAllAsRead, 
    toggleFavorite, 
    fetchFavorites 
} from './ChatService';

const extractConversationsArray = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (res.data && Array.isArray(res.data.conversations)) return res.data.conversations;
    if (res.data && Array.isArray(res.data.content)) return res.data.content;
    if (res.data && Array.isArray(res.data)) return res.data;
    if (res.content && Array.isArray(res.content)) return res.content;
    if (res.list && Array.isArray(res.list)) return res.list;
    if (res.conversations && Array.isArray(res.conversations)) return res.conversations;
    return [];
};

const extractMessagesArray = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (res.data && Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.messages)) return res.data.messages;
    if (res.messages && Array.isArray(res.messages)) return res.messages;
    return [];
};

const extractSearchResult = (res, searchedAppId) => {
    if (!res) return null;
    if (res.applicationId) return res;
    if (res.data) {
        if (res.data.applicationId) return res.data;
        if (Array.isArray(res.data) && res.data.length > 0) {
            const firstMsg = res.data[0];
            const userId = firstMsg.senderType === 'USER' ? firstMsg.senderId : firstMsg.receiverId;
            const latestMsg = res.data[res.data.length - 1];
            return {
                applicationId: searchedAppId,
                userId: userId,
                latestMessage: latestMsg.comment || latestMsg.message,
                addedOn: latestMsg.addedOn,
                senderType: latestMsg.senderType,
                favorite: false,
                read: true
            };
        }
    }
    return null;
};

export default function Main({ pageViewHandler, admin }) {
    const [conversations, setConversations] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [filterType, setFilterType] = useState('all'); // 'all' | 'unread' | 'starred'
    const [searchTerm, setSearchTerm] = useState('');
    const [searchBy, setSearchBy] = useState('appId'); // 'appId' | 'mobile'
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    
    const [loadingConversations, setLoadingConversations] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    
    const empId = admin.emailId;
    const pollIntervalRef = useRef(null);

    // Press Escape to close active chat window (like WhatsApp Web)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setActiveChat(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Initial load and whenever filters change
    useEffect(() => {
        setPage(0);
        loadConversations(0, true);
    }, [filterType, empId]);

    // Setup polling for new messages & unread counts
    useEffect(() => {
        pollIntervalRef.current = setInterval(() => {
            silentReloadConversations();
            if (activeChat) {
                silentReloadMessages();
            }
        }, 8000); // Poll every 8 seconds

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, [activeChat, filterType, empId]);

    const loadConversations = async (pageNum = 0, isReset = true) => {
        if (isReset) {
            setLoadingConversations(true);
        } else {
            setLoadingMore(true);
        }
        try {
            let data = [];
            if (filterType === 'starred') {
                data = await fetchFavorites(empId, pageNum, 15);
            } else {
                const unreadOnly = filterType === 'unread';
                data = await fetchConversations(empId, unreadOnly, pageNum, 15);
            }
            const fetchedArray = extractConversationsArray(data);
            
            if (isReset) {
                setConversations(fetchedArray);
            } else {
                setConversations(prev => [...prev, ...fetchedArray]);
            }
            
            const totalCount = data?.data?.totalCount || data?.totalCount || 0;
            if (totalCount > 0) {
                setHasMore((pageNum + 1) * 15 < totalCount);
            } else {
                setHasMore(fetchedArray.length >= 15);
            }
        } catch (e) {
            console.error('Error loading conversations:', e);
        } finally {
            setLoadingConversations(false);
            setLoadingMore(false);
        }
    };

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        loadConversations(nextPage, false);
    };

    const silentReloadConversations = async () => {
        try {
            let data = [];
            if (filterType === 'starred') {
                data = await fetchFavorites(empId, 0, (page + 1) * 15);
            } else {
                const unreadOnly = filterType === 'unread';
                data = await fetchConversations(empId, unreadOnly, 0, (page + 1) * 15);
            }
            const fetchedArray = extractConversationsArray(data);
            setConversations(fetchedArray.map(c => {
                if (activeChat && (c.applicationId === activeChat.applicationId || c.userId === activeChat.userId)) {
                    return { ...c, unreadCount: 0, read: true };
                }
                return c;
            }));
        } catch (e) {
            console.error('Error in background conversation reload:', e);
        }
    };

    const loadChatHistory = async (chat) => {
        if (!chat || !chat.userId) return;
        setLoadingHistory(true);
        try {
            const res = await loadMessageHistory(chat.userId, 'ADMIN');
            const historyList = extractMessagesArray(res);
            setMessages(historyList);
            
            // Mark all as read (API #5)
            await markAllAsRead(chat.userId, chat.userId);
            if (empId && empId !== chat.userId) {
                try { await markAllAsRead(chat.userId, empId); } catch(err) {}
            }
            
            // Reload conversations to update unread badge in sidebar
            silentReloadConversations();
        } catch (e) {
            console.error('Error loading history:', e);
        } finally {
            setLoadingHistory(false);
        }
    };

    const silentReloadMessages = async () => {
        if (!activeChat || !activeChat.userId) return;
        try {
            const res = await loadMessageHistory(activeChat.userId, 'ADMIN');
            const historyList = extractMessagesArray(res);
            // Check if there are new messages
            if (historyList && historyList.length !== messages.length) {
                setMessages(historyList);
                // Auto mark as read (API #5)
                await markAllAsRead(activeChat.userId, activeChat.userId);
                silentReloadConversations();
            }
        } catch (e) {
            console.error('Error in background messages reload:', e);
        }
    };

    const handleSelectChat = (chat) => {
        // Optimistically clear unread count in conversations list immediately
        setConversations(prev => prev.map(c => {
            if (c.applicationId === chat.applicationId || (c.userId && c.userId === chat.userId)) {
                return { ...c, unreadCount: 0, read: true };
            }
            return c;
        }));

        const updatedChat = { ...chat, unreadCount: 0, read: true };
        setActiveChat(updatedChat);
        loadChatHistory(updatedChat);
    };

    const handleCloseChat = () => {
        setActiveChat(null);
    };

    const handleSendMessage = async (text) => {
        if (!activeChat || !activeChat.userId) return;
        
        // Optimistic update of local UI messages list
        const tempMsg = {
            senderId: 'ADMIN',
            receiverId: activeChat.userId,
            comment: text,
            senderType: 'ADMIN',
            createdAt: new Date().toISOString()
        };
        setMessages((prev) => [...prev, tempMsg]);

        try {
            // Send Message (API #4)
            await sendMessage('ADMIN', activeChat.userId, text);
            // Reload message history to get actual server-synced message list
            silentReloadMessages();
        } catch (e) {
            console.error('Error sending message:', e);
        }
    };

    const handleToggleFavorite = async (appId) => {
        try {
            // Toggle favorite (API #6)
            await toggleFavorite(appId, empId);
            
            // Toggle local favorite status
            setConversations(prev => prev.map(conv => {
                if (conv.applicationId === appId) {
                    const currentFav = conv.favorite || conv.isFavorite;
                    return { ...conv, favorite: !currentFav, isFavorite: !currentFav };
                }
                return conv;
            }));

            if (activeChat && activeChat.applicationId === appId) {
                const currentFav = activeChat.favorite || activeChat.isFavorite;
                setActiveChat(prev => ({ ...prev, favorite: !currentFav, isFavorite: !currentFav }));
            }
        } catch (e) {
            console.error('Error toggling favorite:', e);
        }
    };

    const handleMarkAllAsRead = async (userId) => {
        if (!userId) return;
        try {
            // Mark all as read (API #5)
            await markAllAsRead(userId, userId);
            silentReloadConversations();
            
            if (activeChat && activeChat.userId === userId) {
                silentReloadMessages();
            }
        } catch (e) {
            console.error('Error marking all messages as read:', e);
        }
    };

    // Filter conversations locally based on search term and searchBy mode
    const filteredConversations = Array.isArray(conversations)
        ? conversations.filter(conv => {
            if (!searchTerm) return true;
            const query = searchTerm.toLowerCase();
            if (searchBy === 'mobile') {
                return (
                    conv.mobileNumber?.toLowerCase().includes(query) ||
                    conv.mobile?.toLowerCase().includes(query) ||
                    conv.phoneNumber?.toLowerCase().includes(query)
                );
            }
            return conv.applicationId?.toLowerCase().includes(query);
          })
        : [];

    // Execute API search (API #2) if no local results
    useEffect(() => {
        const triggerSearch = async () => {
            const minLen = searchBy === 'mobile' ? 10 : 5;
            if (searchTerm && filteredConversations.length === 0 && searchTerm.length >= minLen) {
                try {
                    const result = await searchConversation(searchTerm, searchBy);
                    const parsedResult = extractSearchResult(result, searchTerm);
                    if (parsedResult && parsedResult.applicationId) {
                        setConversations([parsedResult]);
                        // If it came with messages array, we can auto-select it!
                        if (result.data && Array.isArray(result.data)) {
                            setActiveChat(parsedResult);
                            setMessages(result.data);
                        }
                    }
                } catch (e) {
                    // Chat search resolution error (ignored for normal search)
                }
            }
        };

        const timer = setTimeout(triggerSearch, 500);
        return () => clearTimeout(timer);
    }, [searchTerm, searchBy, filteredConversations.length]);

    return (
        <div className="chat-container-main">
            <ChatSidebar 
                conversations={filteredConversations}
                activeChat={activeChat}
                onSelectChat={handleSelectChat}
                filterType={filterType}
                setFilterType={setFilterType}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchBy={searchBy}
                setSearchBy={setSearchBy}
                onToggleFavorite={handleToggleFavorite}
                empId={empId}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                loadingMore={loadingMore}
                onRefresh={() => loadConversations(0, true)}
                pageViewHandler={pageViewHandler}
            />

            <ChatWindow 
                activeChat={activeChat}
                messages={messages}
                onSendMessage={handleSendMessage}
                onToggleFavorite={handleToggleFavorite}
                onMarkAllAsRead={handleMarkAllAsRead}
                onCloseChat={handleCloseChat}
                empId={empId}
                loadingHistory={loadingHistory}
                pageViewHandler={pageViewHandler}
            />
        </div>
    );
}
